import {
  useState, useEffect, forwardRef, useImperativeHandle, useReducer, useRef,
} from 'react';
import { nanoid } from 'nanoid';
import { useSearchZustandStore } from '@/helpers/searchctx.js';
import Icon from '@/core/icon.js';
import { useTranslation } from '@/helpers/translate';

const OneChoice = forwardRef(({
  item, onScreen, onFilter, onValid, renderButtonStartTour,
}, ref) => {
  const { t } = useTranslation();
  const inputRef = useRef();
  const [keySlug] = useState(item.id);

  function extractDataFromUrl(values) {
    if (item.skin === 'fieldtext') {
      return values;
    }
    const newValues = values?.split(',') || [];
    return newValues.find((v) => v === item.values?.[0].slug);
  }

  const [currentValue, setCurrentValue] = useState({
    validValue: extractDataFromUrl(useSearchZustandStore.getState()?.initParams?.[item.slug]),
    isChange: null,
  });

  const [isOpenModal, setIsOpenModal] = useReducer((prev, newValue) => {
    let tmpValue = newValue;
    if (!onScreen) return prev;

    if (tmpValue === 'finalclose') {
      tmpValue = false;
    } else if (tmpValue === 'iconclose' || tmpValue === 'outside') {
      // eslint-disable-line no-empty
    } else if (!tmpValue) {
      if (onValid) tmpValue = 'willCloseAndValid';
    }
    return tmpValue;
  }, false);

  function getValue() {
    return currentValue.validValue;
  }

  function getOutput() {
    return {
      keySlug,
      slug: item.slug,
      url: getValue(),
      values: getValue(),
      graphql: {
        [item.slug]: [getValue()],
      },
    };
  }

  function clearValue(runSearch, e) {
    if (e) e.stopPropagation();
    setCurrentValue({ isChange: runSearch ? nanoid() : null, validValue: null });
    setIsOpenModal(false);
  }

  function isSameDefault() {
    return !currentValue?.validValue?.length;
  }

  useImperativeHandle(ref, () => ({
    getValue,
    getOutput,
    clearValue,
    setIsOpenModal,
    isSameDefault,
  }), [currentValue]);

  useEffect(() => {
    if (currentValue.isChange) {
      if (onValid) onValid();
      /* useSearchZustandStore.setState({filterEvent: {
        action:'itemfilteronchoicechange',
        slug: slugEvent}}) */
    }
  }, [currentValue.isChange]);

  useEffect(() => {
    if (isOpenModal === 'willCloseAndValid') {
      onValid();
      setIsOpenModal('finalclose');
    } else if (isOpenModal === 'iconclose' || isOpenModal === 'outside') {
      onValid(isOpenModal);
      setIsOpenModal('finalclose');
    } else if (isOpenModal) {
      if (item.skin === 'fieldtext') {
        inputRef?.current?.focus();
      }
    }
  }, [isOpenModal]);

  function onClick(newValue) {
    if (newValue === getValue()) return setCurrentValue({ validValue: null, isChange: nanoid() });
    return setCurrentValue({ validValue: newValue, isChange: nanoid() });
  }

  function closeOnlyNotSaveValues(action) {
    setCurrentValue({ value: currentValue.validValue });
    setIsOpenModal(action);
  }

  function callrunSearch() {
    setCurrentValue({ isChange: nanoid(), validValue: inputRef.current.value });
    setIsOpenModal(false);
  }

  function onKeyPress(event) {
    if (event.charCode === 13) callrunSearch(event);
  }

  function renderResume() {
    if (!getValue()) return null;
    return (
      <p className="w-full flex justify-center items-center transition h-9 px-[8px] bg-purple rounded-r cursor-pointer">
        <span className="mr-2 text-white" onClick={() => setIsOpenModal(true)}>
          {getValue()}
        </span>
        <button className="w-3" onClick={(e) => { clearValue(true, e); }}><Icon name="close" className="mx-1 w-full text-white fill-current size-1.5" /></button>
      </p>
    );
  }

  function renderTitle() {
    return (
      <div className="flex overflow-hidden justify-between items-center text-sm text-white rounded-lg transition-all duration-200 cursor-pointer bg-purple-6 hover:bg-purple group">
        <p className="truncate flex items-center justify-center w-full text-center py-[8px] px-[8px] min-w-[110px]" onClick={() => setIsOpenModal(true)}>
          <Icon className="mr-2 w-5 h-5 transition-colors duration-200 text-purple-4/70 group-hover:text-white" name={item.slug} />
          <span className="transition-colors duration-200">{t(item.title)}</span>
          <Icon name="next" className="ml-2 w-2 h-2 transition-colors duration-200 transform rotate-90 text-purple-1 group-hover:text-white" />
        </p>
        {renderResume()}
      </div>
    );
  }

  function renderContent() {
    if (item.skin === 'fieldtext') {
      return (
        <p className="flex items-center mt-2 transition">
          <input onKeyPress={onKeyPress} className="px-3 py-2 w-full bg-white rounded-md border cursor-pointer text-purple-6 placeholder-purple-5/50 border-purple-4/40 focus:ring-2 focus:ring-purple focus:ring-offset-0 focus:outline-none focus:border-transparent" placeholder={t('search:placeholder_pattern')} defaultValue={getValue()} ref={inputRef} />
        </p>
      );
    }
    return (
      <p className={`flex justify-center items-center text-sm transition-all duration-200 py-[8px] px-[8px] group rounded-lg cursor-pointer ${currentValue.validValue ? 'bg-purple text-white hover:bg-purple/80' : 'text-white bg-purple-6 hover:bg-purple/70'}`} onClick={() => onClick(item?.values?.[0].slug)}>
        {!item?.values?.[0].icon || <Icon className={`fill-current mr-2 h-5 w-5 transition-colors duration-200 ${currentValue.validValue ? 'text-white' : 'text-purple-4/70 group-hover:text-white'}`} name={item?.values?.[0].icon} />}
        <span className="truncate whitespace-nowrap transition-colors duration-200">{t(item?.values?.[0].title)}</span>
      </p>
    );
  }

  function renderModal() {
    return (
      <>
        <div className="absolute top-0 left-0 z-30 w-screen h-screen bg-purple-6/50" onClick={() => { closeOnlyNotSaveValues('outside'); }} />
        <div className="-mt-[38px]">
          <div className="min-w-[450px] rounded-lg bg-white z-40 absolute px-6 pt-6 pb-4 shadow-button">
          <h1 className="mb-2 font-medium text-purple-6">{t(item.title)}</h1>
            <button
              className="absolute top-4 right-4 size-5"
              onClick={() => {
                closeOnlyNotSaveValues('iconclose');
              }}
            >
              <Icon name="close" className="mx-1 fill-current size-4 text-purple-6/30 hover:text-black" />
            </button>
            {renderContent()}
            <div className="flex justify-between items-center mt-4 mb-2">
              <button className="text-base rounded-none border-b text-purple-5/50 border-purple-5/50 hover:text-purple hover:border-purple" onClick={(e) => clearValue(true, e)}>{t('search:clean')}</button>
              <div>
                {renderButtonStartTour?.()}
                <button className="px-6 py-2 purple-gradient" onClick={() => callrunSearch()}>{t('search:save')}</button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  function renderFilter() {
    return 'onechoice dont have ux for on filter';
  }

  if (!item.modal) return renderContent();

  return (
    <>
      {!(onScreen && !isOpenModal) || renderTitle()}
      {!(onScreen && isOpenModal) || (
      <>
        {renderTitle()}
        {renderModal()}
      </>
      )}
      {!onFilter || renderFilter()}
    </>
  );
});

export default OneChoice;
